// src/redux/services/store/storeCheckoutApi.ts
import type { EndpointBuilder } from "@reduxjs/toolkit/query";
import { baseApi } from "../baseApi";

export type DecimalValue = number | string;

export type StoreCheckoutBuyNowItemIn = {
  variant_id: number;
  qty: number;
  customization_enabled?: boolean;
  customization_color?: string | null;
  customization_note?: string | null;
};

export type StoreCheckoutCreateOrderIn = {
  shipping_address_id: number;
  billing_address_id: number;
  delivery_type_id: number;
  notes?: string | null;
  finalize_cart?: boolean;
  buy_now_item?: StoreCheckoutBuyNowItemIn | null;
};

export type StoreOrderItemOut = {
  id: number;
  product_id?: number | null;
  variant_id?: number | null;
  sku_snapshot: string;
  product_name_snapshot: string;
  variant_name_snapshot?: string | null;
  image_url_snapshot?: string | null;
  qty: DecimalValue;
  unit_price: DecimalValue;
  discount_amount: DecimalValue;
  tax_amount: DecimalValue;
  line_total: DecimalValue;
  customization_enabled?: boolean;
  customization_color?: string | null;
  customization_note?: string | null;
};

export type StoreOrderPaymentLiteOut = {
  id: number;
  provider: string;
  method: string;
  amount: DecimalValue;
  currency_code: string;
  status: string;
  provider_order_id?: string | null;
  provider_payment_id?: string | null;
  attempt_no: number;
  created_at: string;
};

export type StoreOrderOut = {
  id: number;
  order_no: string;
  user_id: number;
  cart_id?: number | null;
  status: string;
  payment_status: string;
  fulfillment_status: string;
  currency_code: string;
  subtotal: DecimalValue;
  discount_total: DecimalValue;
  shipping_total: DecimalValue;
  tax_total: DecimalValue;
  grand_total: DecimalValue;
  notes?: string | null;
  billing_address_snapshot_json: Record<string, any>;
  shipping_address_snapshot_json: Record<string, any>;
  placed_at: string;
  created_at: string;
  updated_at: string;
  items: StoreOrderItemOut[];
  payments: StoreOrderPaymentLiteOut[];
};

export type StoreOrderListOut = {
  items: StoreOrderOut[];
  total: number;
};

type Builder = EndpointBuilder<any, any, any>;

export const storeCheckoutApi = baseApi.injectEndpoints({
  endpoints: (builder: Builder) => ({
    createStoreCheckoutOrder: builder.mutation<
      StoreOrderOut,
      StoreCheckoutCreateOrderIn
    >({
      query: (body) => ({
        url: `/store/checkout/orders`,
        method: "POST",
        body: {
          shipping_address_id: body.shipping_address_id,
          billing_address_id: body.billing_address_id,
          delivery_type_id: body.delivery_type_id,
          notes: body.notes ?? null,
          finalize_cart: body.finalize_cart ?? false,
          buy_now_item: body.buy_now_item ?? null,
        },
      }),
      invalidatesTags: [
        { type: "StoreOrder", id: "LIST" },
        { type: "StoreCart", id: "CART" },
      ],
    }),

    getStoreOrders: builder.query<
      StoreOrderListOut,
      { skip?: number; limit?: number } | void
    >({
      query: (args) => ({
        url: `/store/orders`,
        method: "GET",
        params: {
          skip: (args as any)?.skip ?? 0,
          limit: (args as any)?.limit ?? 20,
        },
      }),
      providesTags: [{ type: "StoreOrder", id: "LIST" }],
    }),

    getStoreOrderById: builder.query<StoreOrderOut, number>({
      query: (orderId) => ({
        url: `/store/orders/${orderId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, orderId) => [{ type: "StoreOrder", id: orderId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateStoreCheckoutOrderMutation,
  useGetStoreOrdersQuery,
  useGetStoreOrderByIdQuery,
} = storeCheckoutApi;
